export enum RBACResource {
  settings = "settings",
  "settings__profile" = "settings__profile",
  "settings__organizations" = "settings__organizations",
  "settings__users" = "settings__users",
  "settings__bots" = "settings__bots",
  "settings__bots__organization" = "settings__bots__organization",
  "settings__ui_preferences" = "settings__ui_preferences",
  "settings__ui_branding" = "settings__ui_branding",
  "organization_admin" = "organization_admin",
  "user_bots" = "user_bots",
  analytics = "analytics",
}

export enum RBACAction {
  read = "read",
  create = "create",
  update = "update",
  delete = "delete",
}

function matchAction(
  permAction: RBACAction | "*" | RBACAction[],
  action: string,
): boolean {
  if (permAction === "*") return true;
  if (Array.isArray(permAction))
    return permAction.includes(action as RBACAction);
  return permAction === action;
}

function matchResource(permResource: string, resource: string): boolean {
  if (permResource === "*") return true;
  if (permResource.endsWith(".*")) {
    const baseResource = permResource.replace(".*", "");
    return resource.startsWith(baseResource);
  }
  return permResource === resource;
}

export type Permission = {
  action: RBACAction | RBACAction[];
  resource: RBACResource;
};

function matchesPermission(
  permission: Permission,
  action: RBACAction,
  resource: RBACResource,
): boolean {
  const { action: permAction, resource: permResource } = permission;

  return !(
    !matchAction(permAction, action) || !matchResource(permResource, resource)
  );
}

export function hasPermission(
  permissions: Permission[],
  action: RBACAction,
  resource: RBACResource,
) {
  for (const perm of permissions) {
    if (matchesPermission(perm, action, resource)) {
      return true;
    }
  }
  return false;
}

type RoleDefinitions = Record<string, Permission[]>;

export enum UserRoles {
  ADMINISTRATOR = "ADMINISTRATOR",
  ORGANIZATION_ADMIN = "ORGANIZATION_ADMIN",
  USER = "USER",
}

export const roles: RoleDefinitions = {
  ADMINISTRATOR: [
    {
      action: "*" as RBACAction,
      resource: "*" as RBACResource,
    },
  ],
  ORGANIZATION_ADMIN: [
    {
      action: [RBACAction.read, RBACAction.update],
      resource: RBACResource["settings__profile"],
    },
    {
      action: [RBACAction.read, RBACAction.update, RBACAction.create],
      resource: RBACResource["settings__bots"],
    },
    {
      action: [RBACAction.read],
      resource: RBACResource.organization_admin,
    },
    {
      action: [RBACAction.read],
      resource: RBACResource.settings__organizations,
    },
    {
      action: [RBACAction.read],
      resource: RBACResource.settings__bots__organization,
    },
    {
      action: [RBACAction.read],
      resource: RBACResource.analytics,
    },
    {
      action: [RBACAction.read, RBACAction.update, RBACAction.create],
      resource: RBACResource["settings__ui_branding"],
    },
  ],
  USER: [
    {
      action: [RBACAction.read, RBACAction.update],
      resource: RBACResource["settings__profile"],
    },
  ],
};
